/**
 * Pure timeline math for the Schedule page (#303): the day window, bar
 * clipping, lane packing for overlapping bars, and the Equipment lens'
 * per-day committed/quote-hold aggregation with its severity verdicts.
 *
 * Everything here is date-string arithmetic (YYYY-MM-DD, UTC-safe) so the
 * route component stays presentational.
 */
import type {
  ScheduleEquipmentLine,
  ScheduleVehicleWindow,
} from "./schedule-api";

/** Today as YYYY-MM-DD in the Company's day — NZ, not the browser's zone. */
export function nzToday(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Pacific/Auckland",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/** ISO date + n days (n may be negative). */
export function shiftISO(iso: string, days: number): string {
  const t = Date.parse(`${iso}T00:00:00Z`);
  if (Number.isNaN(t)) return iso;
  return new Date(t + days * 86_400_000).toISOString().slice(0, 10);
}

/** The window's day columns: `count` consecutive dates from `from`. */
export function windowDays(from: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => shiftISO(from, i));
}

/**
 * Clip an inclusive [start, end] date range to the window, as a column
 * index + span. Null when the range misses the window entirely (a bar the
 * payload should not have sent) or the dates do not parse.
 */
export function clipToWindow(
  start: string,
  end: string | null,
  windowFrom: string,
  dayCount: number,
): { startIdx: number; span: number } | null {
  const s = Date.parse(`${start}T00:00:00Z`);
  const e = Date.parse(`${end || start}T00:00:00Z`);
  const w = Date.parse(`${windowFrom}T00:00:00Z`);
  if (Number.isNaN(s) || Number.isNaN(e) || Number.isNaN(w)) return null;
  const startIdx = Math.round((s - w) / 86_400_000);
  const endIdx = Math.round((Math.max(e, s) - w) / 86_400_000);
  if (endIdx < 0 || startIdx > dayCount - 1) return null;
  const clippedStart = Math.max(0, startIdx);
  const clippedEnd = Math.min(dayCount - 1, endIdx);
  return { startIdx: clippedStart, span: clippedEnd - clippedStart + 1 };
}

export interface Lane<T> {
  item: T;
  startIdx: number;
  span: number;
}

/**
 * Pack bars into lanes so overlapping bars stack instead of colliding:
 * each bar takes the first lane whose last bar ends before it starts.
 * Bars must be pre-sorted by startIdx (the API orders by event_date).
 */
export function assignLanes<T>(bars: Lane<T>[]): Lane<T>[][] {
  const lanes: Lane<T>[][] = [];
  const laneEnds: number[] = [];
  for (const bar of bars) {
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      if (laneEnds[i] < bar.startIdx) {
        lanes[i].push(bar);
        laneEnds[i] = bar.startIdx + bar.span - 1;
        placed = true;
        break;
      }
    }
    if (!placed) {
      lanes.push([bar]);
      laneEnds.push(bar.startIdx + bar.span - 1);
    }
  }
  return lanes;
}

/**
 * One Equipment lens day cell. `firm` counts holds in a phase that reserves,
 * `quote` counts quote-phase holds — which reserve only when the Company
 * sets `quotes_hold_stock` (#314). The split answers "why is it blocked";
 * whether the quote half grades is `quotesReserve` (see cellSeverity).
 */
export interface EquipmentDayCell {
  firm: number;
  quote: number;
  severity: "none" | "committed" | "over";
}

export interface EquipmentRow {
  equipmentId: string;
  name: string;
  stock: number | null;
  allowDoubleBook: boolean;
  lines: ScheduleEquipmentLine[];
  cells: EquipmentDayCell[];
}

/**
 * Severity of one day cell:
 *  - `over`: even the firm holds alone exceed known stock — the picker's
 *    guarantee has been overridden (manual lines, double-book toggles).
 *  - `committed`: everything committed — firm holds fill the stock, or (only
 *    where quotes reserve) the quote holds would tip it over.
 *  - `none`: fits, or no basis for a verdict (unknown stock, double-book
 *    allowed). Missing data degrades honestly — no colour, never a fake
 *    green.
 *
 * `quotesReserve` is the Company's `quotes_hold_stock` (#314). With quotes
 * not reserving, a quote hold must not produce a verdict: the picker will
 * allocate those units to another Booking without complaint, so grading the
 * day committed would tell the Operator a lie the picker then contradicts.
 * The quote count still shows — advisory, not a block.
 */
export function cellSeverity(
  firm: number,
  quote: number,
  stock: number | null,
  allowDoubleBook: boolean,
  quotesReserve: boolean,
): EquipmentDayCell["severity"] {
  if (allowDoubleBook || stock === null) return "none";
  if (firm > stock) return "over";
  const held = quotesReserve ? firm + quote : firm;
  if (held > stock || (firm === stock && firm > 0)) return "committed";
  return "none";
}

/**
 * Group the window-clipped gear lines into Equipment rows with per-day
 * firm/quote sums. Rows come back sorted by name (the API's order), and a
 * row's `lines` keep their API order for the peek panel.
 */
export function buildEquipmentRows(
  lines: ScheduleEquipmentLine[],
  windowFrom: string,
  dayCount: number,
  quotesReserve: boolean,
): EquipmentRow[] {
  const rows = new Map<string, EquipmentRow>();
  for (const line of lines) {
    let row = rows.get(line.equipment_id);
    if (!row) {
      row = {
        equipmentId: line.equipment_id,
        name: line.equipment_name,
        stock: line.stock_quantity,
        allowDoubleBook: line.allow_double_book,
        lines: [],
        cells: Array.from({ length: dayCount }, () => ({
          firm: 0,
          quote: 0,
          severity: "none" as const,
        })),
      };
      rows.set(line.equipment_id, row);
    }
    row.lines.push(line);
    const clip = clipToWindow(
      line.starts_on,
      line.ends_on,
      windowFrom,
      dayCount,
    );
    if (!clip) continue;
    for (let i = clip.startIdx; i < clip.startIdx + clip.span; i++) {
      if (line.derived_phase === "quote") row.cells[i].quote += line.quantity;
      else row.cells[i].firm += line.quantity;
    }
  }
  for (const row of rows.values()) {
    for (const cell of row.cells) {
      cell.severity = cellSeverity(
        cell.firm,
        cell.quote,
        row.stock,
        row.allowDoubleBook,
        quotesReserve,
      );
    }
  }
  return [...rows.values()];
}

/**
 * Minutes past midnight for a free-text time ("7:00 AM", "18:00", "6pm"), or
 * null when it is blank or unparseable. Deliberately the same dialect the Go
 * side parses (internal/allocation) — a window must not read one way in a
 * warning and another on the grid.
 */
export function clockMinutes(value: string | null): number | null {
  if (!value) return null;
  let clean = value.trim().toLowerCase().replace(/\./g, "");
  if (clean === "") return null;
  let meridiem = "";
  if (clean.endsWith("am") || clean.endsWith("pm")) {
    meridiem = clean.slice(-2);
    clean = clean.slice(0, -2).trim();
  }
  const [hhRaw, mmRaw = "0"] = clean.split(":");
  const hh = Number(hhRaw.trim());
  const mm = Number(mmRaw.trim());
  if (!Number.isInteger(hh) || !Number.isInteger(mm)) return null;
  let hour = hh;
  if (meridiem === "pm" && hour < 12) hour += 12;
  if (meridiem === "am" && hour === 12) hour = 0;
  if (hour < 0 || hour > 23 || mm < 0 || mm > 59) return null;
  return hour * 60 + mm;
}

const MINUTES_PER_DAY = 1440;

/**
 * How far into its first day a bar starts and how early in its last day it
 * ends, as fractions of the bar's own width (#305).
 *
 * A vehicle bar spans whole day columns. When the windows on its first and
 * last days carry times, the bar is inset so it draws the hours the truck is
 * actually out instead of the whole day. Anything unknown insets nothing —
 * a missing time reads as whole-day, never as zero, exactly as the overlap
 * check treats it.
 */
export function barInset(
  windows: ScheduleVehicleWindow[],
  days: string[],
  startIdx: number,
  span: number,
): { leftPct: number; rightPct: number } {
  const none = { leftPct: 0, rightPct: 0 };
  if (span <= 0 || windows.length === 0) return none;
  const firstDay = days[startIdx];
  const lastDay = days[startIdx + span - 1];
  if (!firstDay || !lastDay) return none;

  const startMins = clockMinutes(
    windows.find((w) => w.window_date === firstDay)?.start_time ?? null,
  );
  const endMins = clockMinutes(
    windows.find((w) => w.window_date === lastDay)?.end_time ?? null,
  );
  const leadingDayFraction =
    startMins === null ? 0 : startMins / MINUTES_PER_DAY;
  const trailingDayFraction =
    endMins === null ? 0 : (MINUTES_PER_DAY - endMins) / MINUTES_PER_DAY;

  const leftPct = (leadingDayFraction / span) * 100;
  const rightPct = (trailingDayFraction / span) * 100;
  // A bar must stay visible: a same-day window whose times leave nothing to
  // draw is a data problem, and a zero-width bar would hide the booking.
  if (leftPct + rightPct > 90) return none;
  return { leftPct, rightPct };
}

/** The window times of one bar, as a short label ("7:00 AM – 6:00 PM"). */
export function barTimeLabel(
  windows: ScheduleVehicleWindow[],
  days: string[],
  startIdx: number,
  span: number,
): string {
  const firstDay = days[startIdx];
  const lastDay = days[startIdx + span - 1];
  const start =
    windows.find((w) => w.window_date === firstDay)?.start_time ?? null;
  const end = windows.find((w) => w.window_date === lastDay)?.end_time ?? null;
  if (!start && !end) return "";
  return `${start ?? "?"} – ${end ?? "?"}`;
}

/** Does an inclusive [starts_on, ends_on] line cover the given day? */
export function lineCoversDay(
  line: ScheduleEquipmentLine,
  day: string,
): boolean {
  return line.starts_on <= day && day <= line.ends_on;
}
